import { useMemo, useState } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@journ/ui/command'
import { Icons } from '@journ/ui/icons'
import { tv } from '@/libs/tv'
import { useTinykeys } from '@/hooks/use-tinykeys'
import { useIntlayer } from 'react-intlayer'
import { useNavigate } from '@/libs/router'
import { openSheet } from '@/features/ui/store'
import { Sheet } from '@/components/sheet'
import { Container } from '@/components/container'
import { useSearchResults } from '../hooks/use-search-results'
import { useSearch } from '../hooks/use-search'
import type { SearchResult, SearchResultKind, SearchTarget } from '../domain/search'
import { GLOBAL_SHEET_IDS } from '@/features/ui/hooks/use-sheet'

const command = tv({
	base: [
		'h-[75dvh] max-h-200 overflow-auto',
		'place-self-center-safe p-0 w-full bg-background dark:backdrop-blur-md dark:bg-background/75 border border-border',
	],
})

const footer = tv({
	base: 'flex p-3 mt-auto w-full border-t border-border items-center bg-background dark:bg-0c0c0c/05',
})

const footerKey = tv({
	base: 'size-6 select-none items-center border bg-accent flex justify-center',
})

const resultIcon = (kind: SearchResultKind) => {
	const className = 'size-4 dark:text-666666 text-primary shrink-0'
	if (kind === 'customer') return <Icons.Customers className={className} />
	if (kind === 'member') return <Icons.Customers className={className} />
	if (kind === 'workflow' || kind === 'work_item') return <Icons.Transactions className={className} />
	return <Icons.Shortcut className={className} />
}

const groupOrder: SearchResultKind[] = ['shortcut', 'customer', 'member', 'workflow', 'work_item']

const groupResults = (results: SearchResult[]): Record<SearchResultKind, SearchResult[]> => {
	const groups: Record<SearchResultKind, SearchResult[]> = {
		shortcut: [],
		customer: [],
		member: [],
		workflow: [],
		work_item: [],
	}
	for (const result of results) groups[result.kind].push(result)
	return groups
}

type SearchResultItemProps = {
	result: SearchResult
	onSelect: (target: SearchTarget) => void
}

const SearchResultItem = ({ result, onSelect }: SearchResultItemProps) => (
	<CommandItem
		value={result.id}
		onSelect={() => onSelect(result.target)}
		className='text-sm flex flex-col items-start gap-1 py-2 group/item'
	>
		<div className='flex items-center gap-2 w-full'>
			{resultIcon(result.kind)}
			<div className='flex flex-col min-w-0'>
				<span className='truncate'>{result.title}</span>
				{result.description && <span className='truncate text-xs text-muted-foreground'>{result.description}</span>}
			</div>
			<Icons.ArrowOutward className='ml-auto size-4 dark:text-666666 text-primary invisible group-hover/item:visible group-focus/item:visible group-aria-selected/item:visible' />
		</div>
	</CommandItem>
)

const SearchModal = () => {
	const { isOpen, toggle } = useSearch()
	const content = useIntlayer('search')
	const navigate = useNavigate()
	const [term, setTerm] = useState('')
	const { results, isLoading } = useSearchResults(term, isOpen)

	const shortcutResults = useMemo<SearchResult[]>(
		() => [
			{
				id: 'shortcut-overview',
				kind: 'shortcut',
				title: content.shortcuts.overview.value,
				description: '/',
				target: { kind: 'path', path: '/' },
			},
			{
				id: 'shortcut-customers',
				kind: 'shortcut',
				title: content.shortcuts.customers.value,
				description: '/customers',
				target: { kind: 'path', path: '/customers' },
			},
			{
				id: 'shortcut-members',
				kind: 'shortcut',
				title: content.shortcuts.members.value,
				description: '/hr',
				target: { kind: 'path', path: '/hr' },
			},
			{
				id: 'shortcut-time-off',
				kind: 'shortcut',
				title: content.shortcuts.timeOff.value,
				description: '/hr/time-off',
				target: { kind: 'path', path: '/hr/time-off' },
			},
			{
				id: 'shortcut-workflows',
				kind: 'shortcut',
				title: content.shortcuts.workflows.value,
				description: '/tasks/workflows',
				target: { kind: 'path', path: '/tasks/workflows' },
			},
			{
				id: 'shortcut-settings',
				kind: 'shortcut',
				title: content.shortcuts.settings.value,
				description: '/settings/members',
				target: { kind: 'path', path: '/settings/members' },
			},
		],
		[content],
	)

	const visibleShortcuts = useMemo(() => {
		const query = term.trim().toLowerCase()
		return shortcutResults.filter(
			result => !query || result.title.toLowerCase().includes(query) || result.description?.includes(query),
		)
	}, [shortcutResults, term])

	const combinedResults = useMemo(() => [...visibleShortcuts, ...results], [visibleShortcuts, results])
	const groupedResults = useMemo(() => groupResults(combinedResults), [combinedResults])
	const hasResults = combinedResults.length > 0

	useTinykeys({
		'$mod+k': e => {
			e.preventDefault()
			toggle()
		},
	})

	const close = (): void => {
		if (isOpen) toggle()
	}

	const handleSelect = (target: SearchTarget): void => {
		close()

		if (target.kind === 'path') {
			void navigate({ to: target.path })
			return
		}

		if (target.kind === 'customer') {
			openSheet(GLOBAL_SHEET_IDS.CUSTOMER_EDIT, target.id)
			return
		}

		if (target.kind === 'member') {
			openSheet(GLOBAL_SHEET_IDS.MEMBER_DETAILS, target.id)
			return
		}

		if (target.kind === 'workflow') {
			void navigate({ to: '/tasks/$workflow-id', params: { 'workflow-id': target.id } })
			return
		}

		void navigate({
			to: '/tasks/$workflow-id/$work-item-id',
			params: { 'workflow-id': target.workflowId, 'work-item-id': target.workItemId },
		})
	}

	return (
		<Sheet isOpen={isOpen} toggle={toggle}>
			<Container className='h-full'>
				<Command shouldFilter={false} className={command()} style={{ backdropFilter: 'blur(16px)' }}>
					<div className='border-b border-border relative'>
						<CommandInput
							placeholder={content.commandPlaceholder.value}
							className='px-4 h-13.75 py-0'
							onValueChange={setTerm}
						/>
						{isLoading && (
							<div className='absolute bottom-0 h-0.5 w-full overflow-hidden'>
								<div className='absolute top-px h-full w-40 animate-slide-effect bg-linear-to-r dark:from-gray-800 dark:via-white dark:via-80% dark:to-gray-800 from-gray-200 via-black via-80% to-gray-200' />
							</div>
						)}
					</div>
					<CommandList className='scrollbar-hide max-h-none flex-1 px-2'>
						{isLoading && !hasResults && <CommandEmpty>{content.loading}</CommandEmpty>}
						{!isLoading && !hasResults && <CommandEmpty>{content.empty}</CommandEmpty>}
						{groupOrder.map(kind => {
							const items = groupedResults[kind]
							if (items.length === 0) return null
							return (
								<CommandGroup key={kind} heading={content.groups[kind]}>
									{items.map(result => (
										<SearchResultItem key={result.id} result={result} onSelect={target => handleSelect(target)} />
									))}
								</CommandGroup>
							)
						})}
					</CommandList>
					<div className={footer()}>
						<div className='scale-50 dark:opacity-50 -ml-1'>
							<Icons.LogoSmall />
						</div>
						<div className='ml-auto flex space-x-2'>
							{(['ArrowUpward', 'ArrowDownward', 'SubdirectoryArrowLeft'] as const).map(name => {
								const Icon = Icons[name]
								return (
									<div key={name} className={footerKey()}>
										<Icon className='size-3 dark:text-666666 text-black' />
									</div>
								)
							})}
						</div>
					</div>
				</Command>
			</Container>
		</Sheet>
	)
}

export { SearchModal }
